import { isGroupLikeChatType } from '@/modules/chat-matrix-fast/constants/chat'

const STORAGE_KEY = 'matrix_forgotten_rooms_v1'

export type BackendCatalogChatLike = {
  type?: string | null
  matrix_room_id?: string | null
  space_room_id?: string | null
  topics?: Array<{ matrix_room_id?: string | null }>
}

export function isMatrixGroupRoomPruneCandidate(
  chat: BackendCatalogChatLike,
  protectedRoomIds: ReadonlySet<string>,
): boolean {
  const roomId = chat.matrix_room_id?.trim()

  return Boolean(
    roomId
    && !protectedRoomIds.has(roomId)
    && isGroupLikeChatType(chat.type),
  )
}

function readForgottenRoomIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return []
    }

    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter((roomId): roomId is string => typeof roomId === 'string' && roomId.startsWith('!'))
      : []
  } catch {
    return []
  }
}

export function getForgottenRoomIds(): Set<string> {
  return new Set(readForgottenRoomIds())
}

export function rememberForgottenRoom(roomId: string): void {
  if (!roomId.startsWith('!')) {
    return
  }

  const forgotten = getForgottenRoomIds()
  forgotten.add(roomId)
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...forgotten]))
}

/** Убирает room из локального «forgotten» — нужно при повторном инвайте в ранее удалённый чат */
export function restoreForgottenRoom(roomId: string): void {
  if (!roomId.startsWith('!')) {
    return
  }

  const forgotten = readForgottenRoomIds()
  if (!forgotten.includes(roomId)) {
    return
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(forgotten.filter(id => id !== roomId)))
}

export function isForgottenRoom(roomId: string): boolean {
  return getForgottenRoomIds().has(roomId)
}

/** Parent room + space + all topic rooms from REST catalog. */
export function collectBackendCatalogRoomIds(
  backendChats: BackendCatalogChatLike[],
): Set<string> {
  const ids = new Set<string>()
  for (const chat of backendChats) {
    const main = chat.matrix_room_id?.trim()
    if (main) {
      ids.add(main)
    }
    const space = chat.space_room_id?.trim()
    if (space) {
      ids.add(space)
    }
    for (const topic of chat.topics || []) {
      const topicRoom = topic.matrix_room_id?.trim()
      if (topicRoom) {
        ids.add(topicRoom)
      }
    }
  }
  return ids
}

/**
 * Если бекенд всё ещё отдаёт чат — убираем room из локального «forgotten».
 * Иначе после delete/leave чат пропадает из списка навсегда, хотя REST его возвращает.
 */
export function reconcileForgottenRoomsWithBackend(
  backendChats: BackendCatalogChatLike[],
): string[] {
  const backendRoomIds = collectBackendCatalogRoomIds(backendChats)

  if (backendRoomIds.size === 0) {
    return []
  }

  const forgotten = readForgottenRoomIds()
  const restored = forgotten.filter(roomId => backendRoomIds.has(roomId))

  if (!restored.length) {
    return []
  }

  const next = forgotten.filter(roomId => !backendRoomIds.has(roomId))
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next))

  return restored
}
